import React, {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {apiRefreshSession} from 'api-services/AuthService'
import {Loading} from 'components/shared'
import {clearAdminClub, setClub} from 'store/auth/adminClubSlice'
import {onSignInSuccess, onSignOutSuccess} from 'store/auth/sessionSlice'
import {setUser, userLoggedOut} from 'store/auth/userSlice'
import {getIsAuthUser, getUser} from 'store/auth/selectors'
import {
    getAccessTokenFromAuthPayload,
    getAuthPayload,
    getClubFromAuthPayload,
    getUserFromAuthPayload,
} from 'utils/authSession'
import {clearAdminProfile, loadAdminProfile, saveAdminProfile, tokenMatchesAdmin} from 'utils/adminProfileStorage'

const AuthBootstrap = ({children}) => {
    const dispatch = useDispatch()
    const currentUser = useSelector(getUser)
    const currentAuthenticated = useSelector(getIsAuthUser)
    const didRun = useRef(false)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (didRun.current) {
            return
        }

        didRun.current = true

        const bootstrapSession = async () => {
            try {
                const persistedProfile = loadAdminProfile()
                const response = await apiRefreshSession()
                const payload = getAuthPayload(response)
                const accessToken = getAccessTokenFromAuthPayload(payload)
                const user = getUserFromAuthPayload(payload)
                const club = getClubFromAuthPayload(payload)

                if (!accessToken) throw new Error('Admin identity changed after refresh')

                const resolvedUser = currentUser || persistedProfile?.user || user
                if (!resolvedUser || !tokenMatchesAdmin(accessToken, resolvedUser) ||
                    (persistedProfile?.user && !tokenMatchesAdmin(accessToken, persistedProfile.user)) ||
                    (user && !tokenMatchesAdmin(accessToken, user))) {
                    throw new Error('Admin identity changed after refresh')
                }

                dispatch(onSignInSuccess({accessToken}))
                dispatch(setUser(user || resolvedUser))
                if (club || persistedProfile?.club) dispatch(setClub(club || persistedProfile.club))
                if (user) saveAdminProfile(user, club || persistedProfile?.club)
            } catch (error) {
                const status = error?.code || error?.statusCode || error?.status || error?.response?.status
                // 400 can mean no refresh cookie even while an in-memory
                // access token is valid. A fresh tab has no user and signs out.
                // 401 rejects the session; 403 rejects its admin role.
                const definitive = status === 401 || status === 403 || status === 'UNAUTHORIZED_ERROR' || status === 'FORBIDDEN_ERROR' || error.message === 'Admin identity changed after refresh'
                if (definitive) clearAdminProfile()
                if (!currentAuthenticated || definitive) {
                    dispatch(onSignOutSuccess())
                    dispatch(userLoggedOut())
                    dispatch(clearAdminClub())
                }
            } finally {
                setLoading(false)
            }
        }

        bootstrapSession()
    }, [dispatch, currentAuthenticated, currentUser])

    if (loading) {
        return (
            <div className="flex flex-auto flex-col h-[100vh]">
                <Loading loading={true} />
            </div>
        )
    }

    return <>{children}</>
}

export default AuthBootstrap
